/**
 * Portal 入口页的健康检查配置
 * 同时探测：前端静态路径是否可达 + 对应后端 /api/v1/{svc}/health 是否返回 UP
 */

export type HealthStatus = 'checking' | 'up' | 'down' | 'partial' | 'protected';

export interface HealthTarget {
  portalId: string;
  label: string;
  /** 前端应用根路径（用于 HEAD/GET 探活） */
  frontendPath: string;
  /** 后端 health 路径（经 Nginx 代理），空字符串表示不探后端 */
  backendHealthPath: string;
  /** 未登录时不主动请求受保护接口，避免把预期的 401/403 当成健康异常。 */
  requiresAuthentication?: boolean;
}

/**
 * 与 portalConfig.ts 的 id 一一对应。
 * 全量覆盖 9 个子系统 + 1 个 auth 服务（无独立前端）。
 */
export const HEALTH_TARGETS: HealthTarget[] = [
  {
    portalId: 'platform',
    label: '全平台管理',
    frontendPath: '/total-admin/',
    backendHealthPath: '/total-admin/api/v1/total-admin/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'bank',
    label: '银行服务',
    frontendPath: '/bank/',
    backendHealthPath: '/bank/api/v1/bank/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'venue',
    label: '景区管理',
    frontendPath: '/scenic/',
    backendHealthPath: '/scenic/api/v1/scenic/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'exchange',
    label: '大宗合约撮合',
    frontendPath: '/institutional-market/',
    backendHealthPath: '/institutional-market/api/v1/institutional-market/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'admin',
    label: '机构管理后台',
    frontendPath: '/institutional-market-admin/',
    backendHealthPath: '/institutional-market-admin/api/v1/institutional-market-admin/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'market-maker',
    label: '合约运营',
    frontendPath: '/market-maker/',
    backendHealthPath: '/market-maker/api/v1/market-maker/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'regulator',
    label: '监管端',
    frontendPath: '/supervise/',
    backendHealthPath: '/supervise/api/v1/supervise/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'retail',
    label: '消费者票务',
    frontendPath: '/consumer-retail/',
    backendHealthPath: '/consumer-retail/api/v1/consumer-retail/health',
    requiresAuthentication: true,
  },
  {
    portalId: 'retail-admin',
    label: '零售管理后台',
    frontendPath: '/consumer-retail-admin/',
    backendHealthPath: '/consumer-retail-admin/api/v1/consumer-retail-admin/health',
    requiresAuthentication: true,
  },
];

/** 独立探测项：不属于任何 portal 卡片，但会影响所有登录。 */
export const AUXILIARY_TARGETS: HealthTarget[] = [
  {
    portalId: 'auth',
    label: '统一认证 auth-service',
    frontendPath: '',
    backendHealthPath: '/api/v1/auth/health',
  },
];

/** 检查当前同源会话；无会话时不逐个请求受保护的业务健康接口。 */
export async function hasAuthenticatedSession(timeoutMs = 4000): Promise<boolean> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch('/api/v1/auth/check', {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!response.ok) return false;
    const payload = (await response.json()) as { valid?: boolean };
    return payload.valid === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** 对单个目标做前端 + 后端探活，返回合成状态。 */
export async function probeTarget(
  target: HealthTarget,
  timeoutMs = 4000,
  authenticated = false,
): Promise<HealthStatus> {
  const frontendOk = target.frontendPath ? await pingStatus(target.frontendPath, timeoutMs) : 'up';
  if (target.requiresAuthentication && !authenticated) {
    return frontendOk === 'up' ? 'protected' : 'down';
  }
  const backendOk = target.backendHealthPath ? await pingStatus(target.backendHealthPath, timeoutMs) : 'up';
  if (frontendOk === 'up' && backendOk === 'up') return 'up';
  if (frontendOk === 'up' && backendOk === 'protected') return 'protected';
  if (frontendOk === 'down' && backendOk === 'down') return 'down';
  return 'partial';
}

async function pingStatus(path: string, timeoutMs: number): Promise<'up' | 'down' | 'protected'> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(path, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (res.ok) return 'up';
    if (res.status === 401 || res.status === 403) return 'protected';
    return 'down';
  } catch {
    return 'down';
  } finally {
    clearTimeout(timer);
  }
}
